import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import { useCallback, useMemo, useState } from 'react';

interface RefetchAccountDataModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (url: string) => Promise<void>;
  title: string;
  description: string;
  defaultUrl?: string;
}

export default function RefetchAccountDataModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  defaultUrl = '',
}: RefetchAccountDataModalProps) {
  const [url, setUrl] = useState(defaultUrl);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!url.trim()) return;

    setLoading(true);
    try {
      await onConfirm(url);
      onClose();
    } catch (error) {
      console.error('Error refetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [url, onConfirm, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setUrl(defaultUrl);
      onClose();
    }
  }, [loading, defaultUrl, onClose]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: loading,
        onClick: handleClose,
      },
      {
        title: loading ? 'Refetching...' : 'Refetch',
        type: 'primary',
        loading: loading,
        disabled: !url.trim(),
        onClick: handleSubmit,
      },
    ],
    [loading, url, handleClose, handleSubmit],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      description={description}
      actions={actions}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="website-url" className="text-sm font-medium">
            Website URL
          </Label>
          <Input
            id="website-url"
            type="url"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && url.trim() && !loading) {
                handleSubmit();
              }
            }}
            disabled={loading}
            className="w-full"
          />
        </div>
      </div>
    </Modal>
  );
}
